import { useEffect, useRef } from "react";
import { useModelDetail } from "../hooks/useApi";
import CopyButton from "./CopyButton";

interface ModelDetailDrawerProps {
  selectedModel: Record<string, unknown> | null;
  onClose: () => void;
}

interface ModelDetail {
  model_name: string;
  display_name?: string | null;
  provider?: string | null;
  description?: string | null;
  created?: number | null;
  input_modalities?: string[] | null;
  output_modalities?: string[] | null;
  tokenizer?: string | null;
  architecture_modality?: string | null;
  context_length?: number | null;
  max_completion_tokens?: number | null;
  unit_type?: string;
  price_basis?: string; // "per_1m" | "per_second" | "per_image"
  is_free?: boolean;
  capabilities: {
    chat: boolean;
    resp: boolean;
    msg: boolean;
    img: boolean;
    vgen: boolean;
    iim: boolean;
    aim: boolean;
    aom: boolean;
    vim: boolean;
    fim: boolean;
    emb: boolean;
    tr: boolean;
    tts: boolean;
  };
  pricing: {
    prompt?: number | null;
    completion?: number | null;
    image?: number | null;
    request?: number | null;
    web_search?: number | null;
  };
  supported_parameters?: string[] | null;
  default_parameters?: Record<string, unknown> | null;
  platform_fee_percent?: number | null;
  generation?: {
    pricing_skus?: Record<string, string> | null;
    supported_resolutions?: string[] | null;
    supported_aspect_ratios?: string[] | null;
    supported_sizes?: string[] | null;
    supported_durations?: number[] | null;
    supported_frame_images?: string[] | null;
    upscale_factor?: { min?: number; max?: number } | null;
    creativity?: number[] | null;
    generate_audio?: boolean | null;
    seed?: boolean | null;
    allowed_passthrough_parameters?: string[] | null;
    supports_streaming?: boolean | null;
    // Structured ``supported_parameters`` map from the OpenRouter image listing,
    // e.g. {"aspect_ratio": {"type": "enum", "values": [...]}, "n": {"type":
    // "range", "min": 1, "max": 10}}.
    supported_parameters?: Record<string, GenerationParamSpec> | null;
  } | null;
  providers?: ProviderEndpoint[] | null;
  // Third-party benchmark rankings (Design Arena per-arena/category ELO, ranks,
  // win rates; Artificial Analysis indices; future sources) from OpenRouter.
  benchmarks?: Record<string, unknown> | null;
}

interface GenerationParamSpec {
  type?: string;
  values?: Array<string | number>;
  min?: number | string | null;
  max?: number | string | null;
  default?: unknown;
}

// Persian labels for known image generation parameters (aspect_ratio, quality,
// …) exposed by the OpenRouter image listing's ``supported_parameters`` map.
// Unknown parameter keys fall back to the raw key rendered LTR in mono.
const GEN_PARAM_LABELS: Record<string, string> = {
  aspect_ratio: "نسبت ابعاد پشتیبانی‌شده",
  quality: "کیفیت",
  background: "پس‌زمینه",
  n: "تعداد تصاویر",
  input_references: "تصاویر مرجع ورودی",
  output_compression: "فشرده‌سازی خروجی",
  watermark: "نشانهٔ آب",
  seed: "seed",
};

interface ProviderEndpointPricing {
  prompt?: number | null;
  completion?: number | null;
  image?: number | null;
  image_token?: number | null;
  image_output?: number | null;
  request?: number | null;
  input_cache_read?: number | null;
  input_cache_write?: number | null;
  discount?: number | null;
}

interface ProviderEndpoint {
  provider_name?: string | null;
  name?: string | null;
  model_id?: string | null;
  tag?: string | null;
  quantization?: string | null;
  context_length?: number | null;
  max_completion_tokens?: number | null;
  pricing?: ProviderEndpointPricing | null;
  status?: number | null;
  uptime_last_1d?: number | null;
  latency_last_30m?: Record<string, number> | null;
  supports_implicit_caching?: boolean;
}

export type { ModelDetail };

const MODALITY_FA: Record<string, string> = {
  text: "متن",
  image: "تصویر",
  audio: "صدا",
  video: "ویدئو",
  file: "فایل",
  embedding: "امبدینگ",
};

const CAP_LIST: Array<[string, string]> = [
  ["chat", "چت"],
  ["resp", "Responses API"],
  ["msg", "Messages API"],
  ["img", "تولید تصویر"],
  ["vgen", "تولید ویدیو"],
  ["iim", "ورودی تصویر"],
  ["aim", "ورودی صدا"],
  ["aom", "خروجی صدا"],
  ["vim", "ورودی ویدئو"],
  ["fim", "ورودی فایل"],
  ["emb", "امبدینگ"],
  ["tr", "تبدیل گفتار به متن"],
  ["tts", "متن به گفتار"],
];

export { CAP_LIST };

const INFO_TIPS: Record<string, string> = {
  ctx: "حداکثر تعداد توکن‌هایی که مدل در یک درخواست می‌تواند به‌عنوان ورودی دریافت کند (حافظهٔ مدل). درخواست‌های طولانی‌تر از این مقدار رد می‌شوند.",
  mct: "سقف تعداد توکن‌های خروجی که مدل در هر پاسخ می‌تواند تولید کند؛ با پارامتر max_tokens قابل تنظیم و محدودکردن هزینه است.",
};

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtCtx(n: number | null | undefined): string {
  if (!n) return "—";
  return n >= 1e6 ? `${(n / 1e6).toFixed(0)}M` : `${(n / 1e3).toFixed(0)}K`;
}

function cutDecimals(value: number, decimals: number): number {
  const [intPart, decPart = ""] = (value + 1e-12).toFixed(15).split(".");
  return parseFloat(`${intPart}.${decPart.slice(0, decimals)}`);
}

function fmtUsd(v: number | null | undefined): string {
  if (v === null || v === undefined || isNaN(v)) return "—";
  if (v === 0) return "$0";
  if (v < 0.01) return `$${cutDecimals(v, 9).toFixed(9).replace(/0+$/, "")}`;
  if (v < 100) return `$${cutDecimals(v, 2).toFixed(2)}`;
  return `$${Math.trunc(v).toLocaleString("en-US")}`;
}

function providerPrice(
  value: number | null | undefined,
  basis: string,
  marginFactor: number
): number | null {
  if (value == null || isNaN(value)) return null;
  // Mirror the model price table: per-1M tokens for token-priced models,
  // per-unit for per-second / per-image models, with the platform margin
  // applied for display only.
  const scaled = basis === "per_1m" ? value * 1e6 : value;
  return scaled * marginFactor;
}

function modFa(vals: string[] | null | undefined): string {
  return vals && vals.length ? vals.map((v) => MODALITY_FA[v] || v).join(" + ") : "—";
}

export { cap, fmtCtx, fmtUsd, modFa, GEN_PARAM_LABELS };
export type { GenerationParamSpec };

function InfoTip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex group shrink-0">
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-line text-[9px] font-bold leading-none text-text-tertiary cursor-help select-none">
        i
      </span>
      <span className="absolute right-0 top-full mt-1.5 hidden group-hover:block z-30 w-60 px-3 py-2 rounded-btn text-[11px] leading-relaxed whitespace-normal pointer-events-none"
        style={{ background: "rgba(26,26,26,0.92)", color: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}>
        {text}
      </span>
    </span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="text-h3 font-bold text-text-main mb-3">{children}</h4>;
}

interface MetaRowProps {
  label: React.ReactNode;
  info?: string;
  children: React.ReactNode;
  valueClass?: string;
}

function MetaRow({ label, info, children, valueClass = "text-body text-text-main text-left" }: MetaRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-line last:border-0">
      <span className="flex items-center gap-1.5 text-small text-text-secondary shrink-0">
        {label}
        {info && <InfoTip text={info} />}
      </span>
      <span className={`font-mono min-w-0 break-words ${valueClass}`}>{children}</span>
    </div>
  );
}

function ChipList({ items, suffix = "" }: { items: string[]; suffix?: string }) {
  if (!items.length) return <span className="text-body text-text-tertiary">—</span>;
  return (
    <span className="flex flex-wrap gap-1.5 items-start">
      {items.map((it) => (
        <code
          key={it}
          dir="ltr"
          className="font-mono text-mono text-text-secondary bg-surface-hover border border-line rounded px-2 py-0.5"
        >
          {it}
          {suffix && <span className="text-text-tertiary">{suffix}</span>}
        </code>
      ))}
    </span>
  );
}

function YesNo(v: boolean | null | undefined): string {
  if (v === true) return "بله";
  if (v === false) return "خیر";
  return "—";
}

// Persian labels for the Design Arena top-level arenas (models/builders/agents)
// shown in the بررسی فنی benchmark block.
const ARENA_LABELS: Record<string, string> = {
  models: "مدلها",
  builders: "سازندگان",
  agents: "عاملها",
};

// Artificial Analysis index rows shown as scalar scores in the بررسی فنی block.
const AA_INDEX_LABELS: Array<[string, string]> = [
  ["intelligence_index", "شاخص هوش"],
  ["coding_index", "شاخص کدنویسی"],
  ["agentic_index", "شاخص عاملی"],
];

function bmNum(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? v.toLocaleString("en-US") : String(v);
  return String(v);
}

function bmPct(v: unknown): string {
  if (v === null || v === undefined) return "—";
  return `٪${typeof v === "number" && !Number.isInteger(v) ? v.toFixed(1) : String(v)}`;
}

function BenchmarksBlock(benchmarks: Record<string, unknown> | null | undefined) {
  if (!benchmarks || typeof benchmarks !== "object") return null;

  const rawAA = benchmarks.artificial_analysis;
  const aa =
    rawAA && typeof rawAA === "object" && !Array.isArray(rawAA)
      ? (rawAA as Record<string, unknown>)
      : null;

  const designArena = Array.isArray(benchmarks.design_arena)
    ? benchmarks.design_arena.filter((e) => e && typeof e === "object")
    : [];

  const otherKeys = Object.keys(benchmarks).filter(
    (k) => k !== "design_arena" && k !== "artificial_analysis"
  );

  if (!designArena.length && !aa && !otherKeys.length) return null;

  // Group Design Arena entries by top-level arena (models/builders/agents).
  const arenaGroups = new Map<string, Array<Record<string, unknown>>>();
  designArena.forEach((entry) => {
    const arena = String((entry as Record<string, unknown>).arena || "other");
    if (!arenaGroups.has(arena)) arenaGroups.set(arena, []);
    arenaGroups.get(arena)!.push(entry as Record<string, unknown>);
  });

  return (
    <div>
      <SectionTitle>بررسی فنی</SectionTitle>

      {aa && (
        <dl className="divide-y divide-line">
          {AA_INDEX_LABELS.map(([key, label]) => (
            <MetaRow key={key} label={label} valueClass="text-body font-medium text-text-main text-left">
              <span dir="ltr">{bmNum(aa[key])}</span>
            </MetaRow>
          ))}
        </dl>
      )}

      {[...arenaGroups.entries()].map(([arena, entries]) => (
        <div key={arena} className="mb-3">
          <h5 className="text-small font-semibold text-text-secondary mb-2">
            Design Arena · {ARENA_LABELS[arena] || arena}
          </h5>
          <div className="rounded-card border border-line overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-small">
                <thead>
                  <tr className="bg-surface text-text-tertiary border-b border-line">
                    <th className="text-right font-medium px-4 py-2.5 whitespace-nowrap">رده</th>
                    <th className="text-center font-medium px-4 py-2.5 whitespace-nowrap">رتبه</th>
                    <th className="text-center font-medium px-4 py-2.5 whitespace-nowrap">ELO</th>
                    <th className="text-center font-medium px-4 py-2.5 whitespace-nowrap">نرخ برد</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {entries.map((e, idx) => (
                    <tr key={idx} className="bg-surface-base text-text-main">
                      <td className="px-4 py-2.5 font-mono text-left whitespace-nowrap text-text-main" dir="ltr">
                        {String(e.category || "—")}
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono text-text-main" dir="ltr">
                        {e.rank != null ? `#${e.rank}` : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono text-text-main" dir="ltr">
                        {bmNum(e.elo)}
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono text-text-main" dir="ltr">
                        {bmPct(e.win_rate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}

      {otherKeys.length > 0 && (
        <dl className="divide-y divide-line">
          {otherKeys.map((k) => (
            <MetaRow key={k} label={<span className="font-mono" dir="ltr">{k}</span>} valueClass="text-body text-text-main text-left">
              <span className="font-mono text-small text-text-tertiary" dir="ltr">
                {typeof benchmarks[k] === "string" ? benchmarks[k] : JSON.stringify(benchmarks[k])}
              </span>
            </MetaRow>
          ))}
        </dl>
      )}
    </div>
  );
}

export default function ModelDetailDrawer({ selectedModel, onClose }: ModelDetailDrawerProps) {
  const backdropMouseDown = useRef(false);
  const modelName = selectedModel ? (selectedModel.model_name as string) : null;
  const { data: detail, isLoading, isError } = useModelDetail(modelName);

  useEffect(() => {
    if (selectedModel) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [selectedModel]);

  if (!selectedModel) return null;

  const d = detail as ModelDetail | undefined;
  const basis = d?.price_basis || "per_1m";
  const scale = basis === "per_1m" ? 1e6 : 1;

  // The admin-set OpenRouter margin ("ضریب پلتفرم") is added for display only;
  // the raw pricing from the API is never modified.
  const marginFactor = (100 + (d?.platform_fee_percent ?? 0)) / 100;
  const margined = (v: number | null | undefined) => (v == null ? null : v * marginFactor);

  const inUsd = margined(d ? (d.pricing.prompt != null ? d.pricing.prompt * scale : null) : null);
  const outUsd = margined(
    d
      ? d.pricing.completion != null && d.pricing.completion > 0
        ? d.pricing.completion * scale
        : null
      : null
  );

  const endpoints: Array<[string, string]> = [
    ["resp", "/v1/responses"],
    ["msg", "/v1/messages"],
  ];
  if (d?.capabilities.chat) endpoints.push(["chat", "/v1/chat/completions"]);
  if (d?.capabilities.img) endpoints.push(["img", "/v1/images/generations"]);
  if (d?.capabilities.vgen) endpoints.push(["vgen", "/v1/videos/generations"]);
  if (d?.capabilities.emb) endpoints.push(["emb", "/v1/embeddings"]);
  if (d?.capabilities.tr) endpoints.push(["tr", "/v1/audio/transcriptions"]);
  if (d?.capabilities.tts) endpoints.push(["tts", "/v1/audio/speech"]);

  const perLabel =
    basis === "per_second" ? "به‌ازای هر ثانیه" : basis === "per_image" ? "به‌ازای هر تصویر" : "به‌ازای ۱ میلیون توکن";
  // Image models billed per image still report real per-token prompt/completion
  // prices (the text part of an image request). When those differ from the
  // per-image price, the ورودی/خروجی rows and provider table hold per-token
  // values and must be labelled per token — the قيمت هر تصویر row keeps the
  // per-image price.
  const ioPerLabel =
    basis === "per_image" &&
    d &&
    d.pricing.prompt != null &&
    d.pricing.image != null &&
    d.pricing.prompt !== d.pricing.image
      ? "به‌ازای هر توکن"
      : perLabel;
  const defaultParams = d?.default_parameters || {};

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-fade-in"
        onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
        onClick={(e) => { if (e.target === e.currentTarget && backdropMouseDown.current) onClose(); backdropMouseDown.current = false; }}
      />

      <aside className="absolute top-0 bottom-0 left-0 w-full md:w-1/2 bg-surface-base border-r border-line shadow-[0_20px_60px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden animate-drawer-left">
        {/* Header */}
        <div className="bg-surface px-6 pt-5 pb-4 border-b border-line">
          <div className="flex items-start justify-between gap-4" dir="ltr">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-small text-text-tertiary">{d?.provider ? cap(d.provider) : ""}</span>
                {d?.is_free && (
                  <span className="text-[10px] font-medium text-primary bg-primary-light rounded px-1.5 py-0.5">رایگان</span>
                )}
              </div>
              <h2 className="text-h2 text-text-main truncate">{d?.display_name || "—"}</h2>
              <div className="flex items-center gap-1 mt-1.5">
                <code className="font-mono text-small text-text-tertiary ltr block text-left tracking-tight min-w-0 truncate">
                  {d?.model_name || selectedModel.model_name as string}
                </code>
                <CopyButton
                  text={d?.model_name || (selectedModel.model_name as string) || ""}
                  iconOnly
                  title="کپی نام مدل"
                />
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-text-tertiary hover:text-text-main transition-colors p-1 shrink-0"
              aria-label="بستن"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          )}

          {isError && (
            <div className="flex items-center justify-center py-16 text-text-tertiary">
              <p className="text-body">دریافت جزئیات مدل ناموفق بود</p>
            </div>
          )}

          {!isLoading && !isError && d && (
            <>
              {d.description && (
                <p className="text-body text-text-secondary leading-relaxed text-left" dir="ltr">
                  {d.description}
                </p>
              )}

              <div>
                <SectionTitle>مشخصات</SectionTitle>
                <dl className="divide-y divide-line">
                  {d.created != null && (
                    <MetaRow label="تاریخ ایجاد">
                      {new Date(d.created * 1000).toLocaleDateString("fa-IR", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </MetaRow>
                  )}
                  <MetaRow label="معماری" valueClass="text-body text-text-main">
                    <span className="block text-body text-text-main">
                      {modFa(d.input_modalities)} <span className="text-text-tertiary mx-0.5">←</span>{" "}
                      {modFa(d.output_modalities)}
                    </span>
                    <span className="block font-mono text-small text-text-tertiary mt-1 ltr text-left" dir="ltr">
                      {d.architecture_modality || "—"}
                    </span>
                  </MetaRow>
                  <MetaRow label="خروجی‌ها">
                    {d.output_modalities && d.output_modalities.length ? d.output_modalities.join(", ") : "—"}
                  </MetaRow>
                  {d.tokenizer && <MetaRow label="توکن‌ساز">{d.tokenizer}</MetaRow>}
                  <MetaRow label="حافظه (context)" info={INFO_TIPS.ctx}>
                    {fmtCtx(d.context_length)}
                  </MetaRow>
                  <MetaRow label="حداکثر توکن خروجی" info={INFO_TIPS.mct}>
                    {d.max_completion_tokens != null ? Number(d.max_completion_tokens).toLocaleString("en-US") : "—"}
                  </MetaRow>
                </dl>
              </div>

              <div>
                <SectionTitle>قابلیت‌های پشتیبانی‌شده</SectionTitle>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {CAP_LIST.map(([key, label]) => {
                    const on = !!d.capabilities[key as keyof ModelDetail["capabilities"]];
                    return on ? (
                      <span
                        key={key}
                        className="flex items-center gap-1.5 text-small text-primary bg-status-success-bg border border-status-success-text/20 rounded-btn px-2.5 py-1.5"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
                          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {label}
                      </span>
                    ) : (
                      <span
                        key={key}
                        className="flex items-center gap-1.5 text-small text-text-tertiary bg-surface-hover border border-line rounded-btn px-2.5 py-1.5"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
                          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div>
                <SectionTitle>پایان‌پوینت‌های پشتیبانی‌شده</SectionTitle>
                <div className="flex flex-wrap gap-2">
                  {endpoints.map(([, path]) => (
                    <code
                      key={path}
                      dir="ltr"
                      className="flex items-center gap-2 font-mono text-mono text-text-main bg-surface-hover border border-line rounded-btn px-3 py-1.5"
                    >
                      <span className="text-[10px] font-medium text-primary bg-primary-light rounded px-1 py-0.5">POST</span>
                      {path}
                    </code>
                  ))}
                </div>
              </div>

              <div>
                <SectionTitle>تعرفهٔ دقیق (دلاری)</SectionTitle>
                <div className="rounded-card border border-line divide-y divide-line overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-surface">
                    <span className="text-small text-text-secondary">ورودی ({ioPerLabel})</span>
                    <span className="font-mono text-body font-medium text-text-main" dir="ltr">
                      {fmtUsd(inUsd)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 bg-surface">
                    <span className="text-small text-text-secondary">خروجی ({ioPerLabel})</span>
                    <span className="font-mono text-body font-medium text-text-main" dir="ltr">
                      {fmtUsd(outUsd)}
                    </span>
                  </div>
                  {basis === "per_image" && d.pricing.image != null && (
                    <div className="flex items-center justify-between px-4 py-2.5 bg-surface">
                      <span className="text-small text-text-secondary">قیمت هر تصویر</span>
                      <span className="font-mono text-body font-medium text-text-main" dir="ltr">
                        {fmtUsd(margined(d.pricing.image))}
                      </span>
                    </div>
                  )}
                  {d.pricing.request != null && (
                    <div className="flex items-center justify-between px-4 py-2.5 bg-surface">
                      <span className="text-small text-text-secondary">هزینهٔ هر درخواست</span>
                      <span className="font-mono text-body font-medium text-text-main" dir="ltr">
                        {fmtUsd(margined(d.pricing.request))}
                      </span>
                    </div>
                  )}
                  {d.pricing.web_search != null && (
                    <div className="flex items-center justify-between px-4 py-2.5 bg-surface">
                      <span className="text-small text-text-secondary">هزینهٔ هر جست‌وجوی وب</span>
                      <span className="font-mono text-body font-medium text-text-main" dir="ltr">
                        {fmtUsd(margined(d.pricing.web_search))}
                      </span>
                    </div>
                  )}
                  {d.generation?.pricing_skus &&
                    Object.keys(d.generation.pricing_skus).length > 0 && (
                      <>
                        {Object.entries(d.generation.pricing_skus).map(([sku, price]) => (
                          <div key={sku} className="flex items-center justify-between px-4 py-2.5 bg-surface">
                            <span className="font-mono text-small text-text-secondary" dir="ltr">{sku}</span>
                            <span className="font-mono text-body font-medium text-text-main" dir="ltr">
                              {fmtUsd(margined(parseFloat(String(price)) * scale))}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                </div>
              </div>

              {d.supported_parameters && d.supported_parameters.length > 0 && (
                <div>
                  <SectionTitle>پارامترهای قابل تنظیم</SectionTitle>
                  <div className="flex flex-wrap gap-1.5">
                    {d.supported_parameters.map((p) => (
                      <code
                        key={p}
                        dir="ltr"
                        className="font-mono text-mono text-text-secondary bg-surface-hover border border-line rounded px-2 py-0.5"
                      >
                        {p}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {(() => {
                const g = d.generation;
                const hasGen = g && (
                  (g.supported_resolutions?.length) ||
                  (g.supported_aspect_ratios?.length) ||
                  (g.supported_sizes?.length) ||
                  (g.supported_durations?.length) ||
                  (g.supported_frame_images?.length) ||
                  (g.allowed_passthrough_parameters?.length) ||
                  g.upscale_factor ||
                  g.creativity ||
                  g.generate_audio != null ||
                  g.seed != null ||
                  g.supports_streaming != null ||
                  (g.supported_parameters && Object.keys(g.supported_parameters).length > 0)
                );
                if (!hasGen) return null;
                return (
                  <div>
                    <SectionTitle>مشخصات تولید</SectionTitle>
                    <dl className="divide-y divide-line">
                      {g.supported_resolutions?.length ? (
                        <MetaRow label="رزلوشن‌های پشتیبانی‌شده" valueClass="text-body">
                          <ChipList items={g.supported_resolutions} />
                        </MetaRow>
                      ) : null}
                      {g.supported_aspect_ratios?.length ? (
                        <MetaRow label="نسبت ابعاد پشتیبانی‌شده" valueClass="text-body">
                          <ChipList items={g.supported_aspect_ratios} />
                        </MetaRow>
                      ) : null}
                      {g.supported_sizes?.length ? (
                        <MetaRow label="ابعاد پشتیبانی‌شده" valueClass="text-body">
                          <ChipList items={g.supported_sizes} />
                        </MetaRow>
                      ) : null}
                      {g.supported_durations?.length ? (
                        <MetaRow label="مدت‌های پشتیبانی‌شده" valueClass="text-body">
                          <ChipList items={g.supported_durations.map((s) => String(s))} suffix=" ثانیه" />
                        </MetaRow>
                      ) : null}
                      {g.supported_frame_images?.length ? (
                        <MetaRow label="قاب‌های ورودی" valueClass="text-body">
                          <ChipList items={g.supported_frame_images} />
                        </MetaRow>
                      ) : null}
                      {g.supported_parameters && Object.keys(g.supported_parameters).length > 0
                        ? Object.entries(g.supported_parameters).map(([name, spec]) => {
                            const label = GEN_PARAM_LABELS[name] || (
                              <span className="font-mono" dir="ltr">{name}</span>
                            );
                            const s = spec || {};
                            if (Array.isArray(s.values) && s.values.length) {
                              return (
                                <MetaRow key={name} label={label} valueClass="text-body">
                                  <ChipList items={s.values.map((v) => String(v))} />
                                </MetaRow>
                              );
                            }
                            if (s.type === "range" || s.min != null || s.max != null) {
                              return (
                                <MetaRow key={name} label={label}>
                                  {`${s.min ?? "—"} – ${s.max ?? "—"}`}
                                </MetaRow>
                              );
                            }
                            if (s.type === "boolean" || typeof s.default === "boolean") {
                              return (
                                <MetaRow key={name} label={label}>
                                  {YesNo(typeof s.default === "boolean" ? s.default : null)}
                                </MetaRow>
                              );
                            }
                            return null;
                          })
                        : null}
                      {g.upscale_factor ? (
                        <MetaRow label="فاکتور آپاسکیل">
                          {`${g.upscale_factor.min ?? "—"}x – ${g.upscale_factor.max ?? "—"}x`}
                        </MetaRow>
                      ) : null}
                      {g.creativity?.length ? (
                        <MetaRow label="محدوده خلاقیت">
                          {`${g.creativity[0]} – ${g.creativity[1] ?? g.creativity[0]}`}
                        </MetaRow>
                      ) : null}
                      {g.generate_audio != null && (
                        <MetaRow label="تولید صدا در خروجی">
                          {YesNo(g.generate_audio)}
                        </MetaRow>
                      )}
                      {g.seed != null && (
                        <MetaRow label="پشتیبانی از seed">
                          {YesNo(g.seed)}
                        </MetaRow>
                      )}
                      {g.supports_streaming != null && (
                        <MetaRow label="پشتیبانی از streaming">
                          {YesNo(g.supports_streaming)}
                        </MetaRow>
                      )}
                      {g.allowed_passthrough_parameters?.length ? (
                        <MetaRow label="پارامترهای عبور مجاز" valueClass="text-body">
                          <ChipList items={g.allowed_passthrough_parameters} />
                        </MetaRow>
                      ) : null}
                    </dl>
                  </div>
                );
              })()}

              {Object.keys(defaultParams).length > 0 && (
                <div>
                  <SectionTitle>پارامترهای پیش‌فرض</SectionTitle>
                  <dl className="divide-y divide-line">
                    {Object.keys(defaultParams).map((k) => (
                      <MetaRow key={k} label={<span className="font-mono" dir="ltr">{k}</span>}>
                        {String(defaultParams[k])}
                      </MetaRow>
                    ))}
                  </dl>
                </div>
              )}

              {BenchmarksBlock(d.benchmarks)}

              {Array.isArray(d.providers) && d.providers.length > 0 && (
                <div>
                  <SectionTitle>
                    تأمین‌کننده‌ها و قیمت‌گذاری
                    <span className="text-small font-normal text-text-tertiary mr-2">
                      {d.providers.length} تأمین‌کننده
                    </span>
                  </SectionTitle>
                  <p className="text-small text-text-tertiary mb-3">
                    ورودی / خروجی {ioPerLabel}
                  </p>
                  <div className="rounded-card border border-line overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-small">
                        <thead>
                          <tr className="bg-surface text-text-tertiary border-b border-line">
                            <th className="text-right font-medium px-4 py-2.5 whitespace-nowrap">تأمین‌کننده</th>
                            <th className="text-left font-medium px-4 py-2.5 whitespace-nowrap">ورودی</th>
                            <th className="text-left font-medium px-4 py-2.5 whitespace-nowrap">خروجی</th>
                            <th className="text-left font-medium px-4 py-2.5 whitespace-nowrap">کانتکست</th>
                            <th className="text-center font-medium px-4 py-2.5 whitespace-nowrap">کش</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                          {d.providers.map((p, idx) => {
                            const pr = p.pricing || {};
                            // Image-generation providers bill per image
                            // token/output: OpenRouter reports prompt/completion
                            // as $0 for them. Mirror the model-level image
                            // pricing map so ورودی/خروجی show the real price.
                            const inVal =
                              (pr.prompt != null && pr.prompt > 0)
                                ? pr.prompt
                                : (pr.image_token != null && pr.image_token > 0)
                                  ? pr.image_token
                                  : (pr.image != null && pr.image > 0)
                                    ? pr.image
                                    : pr.image_output;
                            const outVal =
                              (pr.completion != null && pr.completion > 0)
                                ? pr.completion
                                : (pr.image_output != null && pr.image_output > 0)
                                  ? pr.image_output
                                  : (pr.image_token != null && pr.image_token > 0)
                                    ? pr.image_token
                                    : pr.image;
                            const inPrice = providerPrice(inVal, basis, marginFactor);
                            const outPrice = providerPrice(outVal, basis, marginFactor);
                            const discount = p.pricing?.discount;
                            const hasDiscount = discount != null && discount > 0;
                            return (
                              <tr key={idx} className="bg-surface-base text-text-main">
                                <td className="px-4 py-3 align-top">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-text-main">
                                      {p.provider_name || p.name || "—"}
                                    </span>
                                    {p.quantization && (
                                      <code className="font-mono text-[10px] text-text-secondary bg-surface-hover border border-line rounded px-1.5 py-0.5" dir="ltr">
                                        {p.quantization}
                                      </code>
                                    )}
                                    {hasDiscount && (
                                      <span className="text-[10px] font-medium text-status-success-text bg-status-success-bg rounded px-1.5 py-0.5">
                                        ٪{Math.round(discount * 100)} تخفیف
                                      </span>
                                    )}
                                  </div>
                                  {p.tag && (
                                    <div className="font-mono text-small text-text-tertiary mt-0.5 text-left" dir="ltr">
                                      {p.tag}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 font-mono text-left whitespace-nowrap text-text-main" dir="ltr">
                                  {fmtUsd(inPrice)}
                                </td>
                                <td className="px-4 py-3 font-mono text-left whitespace-nowrap text-text-main" dir="ltr">
                                  {fmtUsd(outPrice)}
                                </td>
                                <td className="px-4 py-3 font-mono text-left whitespace-nowrap text-text-main" dir="ltr">
                                  {fmtCtx(p.context_length)}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {p.supports_implicit_caching ? (
                                    <span className="inline-flex text-[10px] font-medium text-primary bg-primary-light rounded px-1.5 py-0.5">
                                      کش
                                    </span>
                                  ) : (
                                    <span className="text-text-tertiary">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}